import type {
    InstitutionIconSize,
    InstitutionIconSource,
} from "@design-system/InstitutionIcon/InstitutionIcon";
import {
    institutionCatalogue,
    type InstitutionKey,
} from "@design-system/InstitutionIcon/institutionCatalogue";
import { financeInstitutions } from "@design-system/InstitutionIcon/institutionCatalogueFinance";
import { productInstitutions } from "@design-system/InstitutionIcon/institutionCatalogueProduct";
import { providerInstitutions } from "@design-system/InstitutionIcon/institutionCatalogueProviders";

/** Largest first, in Figma's column order. */
export const institutionIconSizes: readonly InstitutionIconSize[] = [
    "xl",
    "lg",
    "md",
    "sm",
    "xs",
];

export const institutionIconSizePixels: Record<InstitutionIconSize, number> = {
    xs: 16,
    sm: 20,
    md: 24,
    lg: 32,
    xl: 40,
};

const isInstitutionKey = (key: string): key is InstitutionKey =>
    key in institutionCatalogue;

/** Catalogue keys grouped as the catalogue files group them. */
export const institutionSections = [
    {
        id: "finance",
        title: "Figma: banks, cards and payments",
        keys: Object.keys(financeInstitutions).filter(isInstitutionKey),
    },
    {
        id: "providers",
        title: "Figma: Kick, providers and AI",
        keys: Object.keys(providerInstitutions).filter(isInstitutionKey),
    },
    {
        id: "product",
        title: "Not in Figma yet (question D5): today's artwork",
        keys: Object.keys(productInstitutions).filter(isInstitutionKey),
    },
];

/** Stands in for a Plaid or integration logo URL. */
export const logoUrlSample = `data:image/svg+xml,${encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"><rect width="40" height="40" fill="#1F3A5F"/><path d="M12 28V17l8-5 8 5v11h-5v-7h-6v7z" fill="#fff"/></svg>',
)}`;

export const brokenLogoUrlSample = "data:image/png;base64,broken";

/** One stack member: a React key, an accessible name and an `InstitutionIcon` source. */
export interface PlaygroundInstitution {
    id: string;
    label: string;
    source: InstitutionIconSource;
}

interface PlaygroundInstitutionStack {
    id: string;
    items: readonly PlaygroundInstitution[];
    extraCount: number;
}

const fromCatalogue = (key: InstitutionKey): PlaygroundInstitution => ({
    id: key,
    label: institutionCatalogue[key].name,
    source: { institution: key },
});

const fromCatalogueKeys = (keys: readonly InstitutionKey[]) =>
    keys.map(fromCatalogue);

/** The billing plan card's "+50", connected banks (one with no logo, one broken) and AI actors. */
export const institutionStacks: readonly PlaygroundInstitutionStack[] = [
    {
        id: "billing +50",
        items: fromCatalogueKeys([
            "stripe",
            "paypal",
            "mercury",
            "ramp",
            "plaid",
        ]),
        extraCount: 50,
    },
    {
        id: "banks",
        items: [
            fromCatalogue("chase"),
            {
                id: "no-logo",
                label: "First Community Bank",
                source: { src: null },
            },
            {
                id: "broken",
                label: "Harbor Credit Union",
                source: { src: brokenLogoUrlSample },
            },
            fromCatalogue("bank-of-america"),
            fromCatalogue("ally"),
        ],
        extraCount: 0,
    },
    {
        id: "AI",
        items: fromCatalogueKeys([
            "claude",
            "chatgpt",
            "cursor",
            "gemini",
            "copilot",
            "grok",
        ]),
        extraCount: 0,
    },
];
